/*
 * Promise wrappers around the Office.js mailbox callbacks we need, plus the
 * notification bar helpers used to report progress from a function command.
 */

/* global Office, console */

/** Outlook truncates notification text past this length. */
const MAX_NOTIFICATION_LENGTH = 150;

/** The compose item, or undefined when the add-in was invoked outside compose. */
export function getComposeItem(): Office.MessageCompose | undefined {
  const item = Office.context?.mailbox?.item as unknown as Office.MessageCompose | undefined;
  if (!item) {
    console.error("[zip] Office.context.mailbox.item is unavailable at dispatch");
    return undefined;
  }
  if (typeof item.addFileAttachmentFromBase64Async !== "function") {
    console.error("[zip] compose item lacks the Mailbox 1.8 attachment API");
    return undefined;
  }
  return item;
}

export function getSubject(item: Office.MessageCompose): Promise<string> {
  return new Promise((resolve) => {
    item.subject.getAsync((result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "");
    });
  });
}

/** Compose mode has no synchronous attachments collection - this is the 1.8 API. */
export function getAttachments(
  item: Office.MessageCompose
): Promise<Office.AttachmentDetailsCompose[]> {
  return new Promise((resolve, reject) => {
    item.getAttachmentsAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || []);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
}

export function getAttachmentContent(
  item: Office.MessageCompose,
  attachmentId: string
): Promise<Office.AttachmentContent> {
  return new Promise((resolve, reject) => {
    item.getAttachmentContentAsync(attachmentId, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
}

export function addBase64Attachment(
  item: Office.MessageCompose,
  base64: string,
  name: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    item.addFileAttachmentFromBase64Async(base64, name, { isInline: false }, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
}

export function removeAttachment(item: Office.MessageCompose, attachmentId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    item.removeAttachmentAsync(attachmentId, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error.message));
      }
    });
  });
}

function replaceNotification(
  key: string,
  details: Office.NotificationMessageDetails
): Promise<void> {
  return new Promise((resolve) => {
    const messages = Office.context?.mailbox?.item?.notificationMessages;
    if (!messages) {
      // The notification bar is the only channel these commands have. Losing it
      // silently makes every downstream failure look like a dead button, so say
      // so where a debugger can see it.
      console.error(`[zip:${key}] no notification surface for: ${details.message}`);
      resolve();
      return;
    }
    // Notification failures are cosmetic - never let them break the command.
    try {
      messages.replaceAsync(key, details, (result) => {
        if (result?.status === Office.AsyncResultStatus.Failed) {
          console.error(`[zip:${key}] notification rejected: ${result.error.message}`);
        }
        resolve();
      });
    } catch (error) {
      console.error(`[zip:${key}] notification threw: ${(error as Error).message}`);
      resolve();
    }
  });
}

function trim(message: string): string {
  return message.length > MAX_NOTIFICATION_LENGTH
    ? message.slice(0, MAX_NOTIFICATION_LENGTH - 1) + "\u2026"
    : message;
}

export function notifyProgress(key: string, message: string): Promise<void> {
  return replaceNotification(key, {
    type: Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator,
    message: trim(message),
  });
}

export function notifyInfo(key: string, message: string): Promise<void> {
  return replaceNotification(key, {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: trim(message),
    icon: "Icon.80x80",
    persistent: false,
  });
}

export function notifyError(key: string, message: string): Promise<void> {
  return replaceNotification(key, {
    type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
    message: trim(message),
  });
}

export function clearNotification(key: string): Promise<void> {
  return new Promise((resolve) => {
    const messages = Office.context?.mailbox?.item?.notificationMessages;
    if (!messages) {
      resolve();
      return;
    }
    try {
      messages.removeAsync(key, () => resolve());
    } catch {
      resolve();
    }
  });
}
